import { useEffect, useRef } from "react";
import { useInView, useMotionValue, useTransform, animate, motion } from "framer-motion";

/**
 * Animated number counter that runs once when scrolled into view.
 * Uses framer-motion useTransform so updates bypass React render cycle (no setState).
 */
export default function AnimatedCounter({ value, duration = 1.6, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  // Try to parse a leading numeric portion. If none, just render the literal value.
  const match = String(value).match(/^([\d,]+(?:\.\d+)?)(.*)$/);
  const numericString = match ? match[1].replace(/,/g, "") : null;
  const suffix = match ? match[2] || "" : "";
  const target = numericString !== null ? parseFloat(numericString) : NaN;
  const isNumeric = !Number.isNaN(target);

  const motionValue = useMotionValue(0);
  const formatted = useTransform(motionValue, (latest) => {
    if (!isNumeric) return String(value);
    const out = target % 1 === 0 ? Math.round(latest).toLocaleString() : latest.toFixed(1);
    return out + suffix;
  });

  useEffect(() => {
    if (!inView || !isNumeric) return;
    const controls = animate(motionValue, target, {
      duration,
      ease: [0.16, 1, 0.3, 1],
    });
    return () => controls.stop();
  }, [inView, isNumeric, target, motionValue, duration]);

  if (!isNumeric) {
    return (
      <span ref={ref} className={className} data-testid="animated-counter">
        {String(value)}
      </span>
    );
  }

  return (
    <motion.span ref={ref} className={className} data-testid="animated-counter">
      {formatted}
    </motion.span>
  );
}
